import { ArrowSmallIcon } from 'components/icons/ArrowSmallIcon'
import AssistantIcon from 'components/icons/AssistantIcon'
import DrawingControlsIcon from 'components/icons/DrawingControlsIcon'
import LayersIcon from 'components/icons/LayersIcon'
import ZapIcon from 'components/icons/ZapIcon'
import type React from 'react'
import ModalWindow from '../ModalWindow/ModalWindow'
import './WhatsNewModal.scss'

interface WhatsNewModalProps {
  isOpened: boolean
  onCloseModal: () => void
}

const RELEASE_DATE = 'August 2026'

type ReleaseItem = {
  name: string
  icon: React.ReactNode
  description?: string
  example?: React.ReactNode
  points?: string[]
  guide?: { slug: string; label: string }
}

const ITEMS: ReleaseItem[] = [
  {
    name: 'Smart Scopes, rebuilt',
    icon: <ZapIcon width={20} height={20} color="currentColor" />,
    description:
      'Draw a wall once, then stack the scopes that make up its real assembly. Each scope asks only its own takeoff questions and generates its own quantities for Quantities and Estimate.',
    example: (
      <>
        <strong>Example:</strong> Brick Veneer → CI → Air Barrier → Wall Ties → Sheathing → Framing → Cavity Insulation →
        Drywall
      </>
    ),
    guide: { slug: 'smart-scopes', label: 'How Smart Scopes work' },
  },
  {
    name: 'Drawing sets & revisions',
    icon: <LayersIcon width={20} height={20} color="currentColor" />,
    description: 'Upload addendums, switch between drawing sets, and overlay revisions to see exactly what changed.',
    guide: { slug: 'drawing-revisions', label: 'How drawing sets and revisions work' },
  },
  {
    name: 'Congic Assistant',
    icon: <AssistantIcon width={20} height={20} color="currentColor" />,
    description:
      'Your AI assistant is now built directly into the project. Ask questions about your drawings, quantities, pricing, or construction work, while the planner keeps you moving from scale check through takeoff, estimate, and proposal.',
    guide: { slug: 'congic-assistant', label: 'Getting started with Congic Assistant' },
  },
  {
    name: 'Drawing & selection',
    icon: <DrawingControlsIcon width={20} height={20} color="currentColor" />,
    description:
      'Drawing controls are now consolidated in the toolbar, with improved multi-select, box selection, and panning while shapes are selected.',
  },
]

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ isOpened, onCloseModal }) => {
  const openGuide = (slug: string) => {
    // Open before closing so the call stays inside the click gesture.
    window.open(`/guides/${slug}`, '_blank', 'noopener')
    onCloseModal()
  }

  return (
    <ModalWindow
      isOpened={isOpened}
      isCloseButton={true}
      onCloseModal={onCloseModal}
      closeButtonPlacement={{ top: '22px', right: '22px' }}
      className="whats-new-modal"
      dataTest="whats-new-modal"
    >
      {/* Plain divs, not h2/h3/p — global `!important` tag rules in src/styles.scss override those. */}
      <div className="whats-new-modal__head">
        <span className="whats-new-modal__eyebrow">{RELEASE_DATE}</span>
        <div className="whats-new-modal__title">What's new in TakeCost</div>
        <div className="whats-new-modal__subtitle">Here's what changed, and where to read more.</div>
      </div>

      <div className="whats-new-modal__content">
        <ul className="whats-new-modal__list">
          {ITEMS.map((item) => (
            <li className="whats-new-modal__item" data-test="whats-new-item" key={item.name}>
              <span className="whats-new-modal__item-icon">{item.icon}</span>
              <div className="whats-new-modal__item-body">
                <div className="whats-new-modal__item-name">{item.name}</div>
                {item.description && <div className="whats-new-modal__item-description">{item.description}</div>}
                {item.example && <div className="whats-new-modal__item-example">{item.example}</div>}
                {item.points && (
                  <ul className="whats-new-modal__item-points">
                    {item.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}
                {item.guide && (
                  <button
                    type="button"
                    className="whats-new-modal__guide-link"
                    data-test="whats-new-guide-link"
                    data-test-slug={item.guide.slug}
                    onClick={() => item.guide && openGuide(item.guide.slug)}
                  >
                    {item.guide.label}
                    <ArrowSmallIcon color="currentColor" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </ModalWindow>
  )
}

export default WhatsNewModal
